import * as React from 'react'

import { DETAIL_SHEET_CLASS } from '@/components/detailSheet'
import { SheetTabStrip } from '@/components/SheetTabStrip'
import { Sheet, SheetContent, SheetDescription } from '@/components/ui/sheet'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

import { ImpactConfirmDialog } from '../ImpactConfirmDialog'
import { SnippetUsageTab } from '../SnippetUsageTab'
import { SnippetVariantsTab } from '../SnippetVariantsTab'
import { hidesFromOtherOrgs } from '../drafts'
import type { useSnippetEditorSheet } from '../hooks/useSnippetEditorSheet'
import { openDiffFor, otherOrgs } from '../utils'
import { ContentTab } from './ContentTab'
import type { DiffMode } from './CompareView'
import { DiscardDialog } from '@/components/DiscardDialog'
import { DrillIn, DrillTab } from './DrillIn'
import { SnippetSheetFooter } from './SheetFooter'
import { SnippetSheetHeader } from './SheetHeader'
import { SnippetImpactPanel } from './SnippetImpactPanel'
import { sheetModel, type TabKey } from './model'
import { useSheetNavigation } from './useSheetNavigation'
import { viewForTab } from './view'

type SnippetEditorSheetProps = ReturnType<
  typeof useSnippetEditorSheet
>['sheetProps'] & {
  /** Read-only Careforce sheet: open the same snippet in customize mode. */
  onCustomizeRequest?: () => void
}

export function SnippetEditorSheet(props: SnippetEditorSheetProps) {
  const { draft, view, onViewChange, variants, usage, isSaving } = props
  const [diffMode, setDiffMode] = React.useState<DiffMode>('unified')
  const primaryRef = React.useRef<HTMLButtonElement>(null)
  const onDiffModeChange = useMediaQuery('(max-width: 640px)')
    ? undefined
    : setDiffMode

  const model = draft && sheetModel(draft, { variants, usage })
  const canSave =
    !draft?.readOnly &&
    (Boolean(draft?.name.trim()) || Boolean(draft?.variant)) &&
    !isSaving
  const showsOrgs = model?.tabs.some((tab) => tab.value === 'orgs') ?? false
  const openDiff = openDiffFor(view.tab === 'content' ? null : view.open, {
    savedContent: draft?.savedContent ?? draft?.content ?? '',
    variants,
    usage,
    showsOrgs,
  })
  const listLabel = model?.tabs.find((tab) => tab.value === view.tab)?.label

  const nav = useSheetNavigation(view, onViewChange, openDiff !== null)
  const drillIn = openDiff && listLabel && (
    <DrillIn
      listLabel={listLabel}
      diff={openDiff}
      mode={diffMode}
      onModeChange={onDiffModeChange}
      backRef={nav.backRef}
      onBack={nav.back}
    />
  )
  const currentDutyId = draft?.variant?.dutyId

  return (
    <Sheet open={draft !== null} onOpenChange={props.onOpenChange}>
      <SheetContent
        ref={nav.sheetRef}
        data-testid="snippet-editor-sheet"
        side="right"
        showCloseButton={false}
        onEscapeKeyDown={(event) => {
          if (!nav.canGoBack) return
          event.preventDefault()
          nav.back()
        }}
        // Radix focuses the first tabbable element, which on a read-only
        // row is a badge whose tooltip would then cover the title.
        onOpenAutoFocus={(event) => {
          if (model?.nameEditable) return
          event.preventDefault()
          nav.sheetRef.current?.focus()
        }}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            if (!canSave) return
            event.preventDefault()
            props.onSave()
          }
        }}
        className={cn(DETAIL_SHEET_CLASS, 'flex flex-col overflow-hidden')}>
        <SheetDescription className="sr-only">
          {model?.description}
        </SheetDescription>
        <Tabs
          value={view.tab}
          onValueChange={(tab) => onViewChange(viewForTab(tab as TabKey))}
          className="min-h-0 flex-1">
          <div className="border-b border-neutral-subtle-border">
            <SnippetSheetHeader
              model={model}
              name={draft?.name ?? ''}
              nameError={props.nameError}
              onNameChange={props.onNameChange}
              sharing={{
                isShared: draft?.isShared === true,
                isHidden: hidesFromOtherOrgs(draft),
                disabled: isSaving,
                onSharedChange: props.onSharedChange,
                onHiddenChange: props.onHiddenChange,
              }}
            />
            {model && (
              <div className="px-4">
                <SheetTabStrip tabs={model.tabs} />
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 [scrollbar-gutter:stable]">
            {/* forceMount: remounting the Milkdown editor on every tab
                flip throws away the caret, undo stack and scroll. */}
            <TabsContent value="content" forceMount className="flex-col gap-3">
              <ContentTab
                model={model}
                editorKey={props.editorKey}
                onContentChange={props.onContentChange}
                toggleRef={nav.contentToggleRef}
                comparing={view.tab === 'content' && view.compare}
                onCompareChange={nav.showCompare}
                base={props.compareBase}
                content={draft?.content ?? ''}
                onUseBase={() => nav.leaveCompare(props.onUseBase)}
                mode={diffMode}
                onModeChange={onDiffModeChange}
              />
            </TabsContent>
            <DrillTab value="variants" drillIn={drillIn}>
              <SnippetVariantsTab
                variants={variants}
                isLoading={props.isLoadingVariants}
                error={props.variantsError}
                onRetry={props.onRetryVariants}
                currentDutyId={currentDutyId}
                onOpen={(row) =>
                  // This duty's own row is the draft: compare it in place.
                  row.dutyId === currentDutyId
                    ? nav.showCompare(true)
                    : nav.drillInto({ tab: 'variants', open: row })
                }
              />
            </DrillTab>
            {showsOrgs && (
              <DrillTab value="orgs" drillIn={drillIn}>
                <SnippetUsageTab
                  orgs={usage && otherOrgs(usage)}
                  error={props.usageError}
                  onRetry={props.onRetryUsage}
                  onOpen={(row) => nav.drillInto({ tab: 'orgs', open: row })}
                />
              </DrillTab>
            )}
          </div>
        </Tabs>
        {model?.impact && (
          <SnippetImpactPanel
            reach={model.impact.reach}
            strip={props.impactStrip}
            onCheck={props.onCheckImpact}
          />
        )}
        <SnippetSheetFooter
          footer={model?.footer ?? null}
          resets={{
            name: draft?.name ?? '',
            instanceCount: draft?.instanceCount ?? 0,
            variantCount: variants?.length ?? draft?.variantCount ?? 0,
            isResetting: props.isResetting,
            onResetVariant: props.onResetVariant,
            onResetToCareforce: props.onResetToCareforce,
          }}
          isDirty={props.isDirty}
          isSaving={isSaving}
          canSave={canSave}
          onSave={props.onSave}
          onCustomize={props.onCustomizeRequest}
          onDismiss={() => props.onOpenChange(false)}
          primaryRef={primaryRef}
        />
      </SheetContent>
      <DiscardDialog
        open={props.isConfirmingDiscard}
        includesSharing={model?.sharing != null}
        onDiscard={props.onDiscardChanges}
        onKeepEditing={props.onKeepEditing}
      />
      <ImpactConfirmDialog
        impact={props.confirmingImpact}
        onSaveAnyway={props.onSaveAnyway}
        onCancel={props.onCancelImpact}
        returnFocusRef={primaryRef}
      />
    </Sheet>
  )
}
